import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup, TablePagination } from '@mui/material';
import { RestartAlt as RestartAltIcon } from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartConfig } from './ChartConfig';

const InspectionsCharts = ({ 
  inspectionsData, 
  onSortInspectionsNotCompliant,
  inspectionsNotCompliantSortField, 
  inspectionsNotCompliantSortDirection,
  onResetInspectionsNotCompliant,
  inspectionsFilter,
  setInspectionsFilter,
  resetInspectionsFilter,
  hiddenInspectionLines,
  setHiddenInspectionLines,
  resetHiddenInspectionLines
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const chartConfig = useChartConfig();

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Check if inspections have data for each filter
  const inspectionsDataNotCompliant = inspectionsData.filter(record => {
    const complianceColumn = record['Compliance'] !== undefined ? 'Compliance' :
                           record.compliance !== undefined ? 'compliance' : null;
    return complianceColumn && record[complianceColumn] && 
           record[complianceColumn].toString().toUpperCase() === 'NO';
  });
  const inspectionsDataAll = inspectionsData; // All inspections
  const hasInspectionsNotCompliant = inspectionsDataNotCompliant.length > 0;
  const hasInspectionsAll = inspectionsDataAll.length > 0;
  const hasAnyInspections = hasInspectionsNotCompliant || hasInspectionsAll;

  // Ensure the current filter is valid (has data)
  // If current filter has no data, switch to the other one that has data
  const validInspectionsFilter = (() => {
    if (inspectionsFilter === 'not_compliant' && !hasInspectionsNotCompliant && hasInspectionsAll) {
      return 'all';
    }
    if (inspectionsFilter === 'all' && !hasInspectionsAll && hasInspectionsNotCompliant) {
      return 'not_compliant';
    }
    return inspectionsFilter;
  })();

  // Update filter state if it's invalid
  useEffect(() => {
    if (validInspectionsFilter !== inspectionsFilter) {
      setInspectionsFilter(validInspectionsFilter);
    }
  }, [validInspectionsFilter, inspectionsFilter, setInspectionsFilter]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Chart 1: Inspections Over Time */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Inspections Over Time
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenInspectionLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <ComposedChart 
            data={(() => {
              // Group data by month and calculate monthly counts
              const monthlyData = {};
              
              inspectionsData.forEach(record => {
                const dateColumn = record['Inspection Date'] !== undefined ? 'Inspection Date' :
                                 record.inspection_date !== undefined ? 'inspection_date' : null;
                
                if (dateColumn && record[dateColumn]) {
                  const date = new Date(record[dateColumn]);
                  if (!isNaN(date.getTime())) {
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    
                    if (!monthlyData[monthKey]) {
                      monthlyData[monthKey] = {
                        month: monthKey,
                        totalInspections: 0,
                        compliantInspections: 0
                      };
                    }
                    
                    monthlyData[monthKey].totalInspections++;
                    
                    // Check compliance
                    const complianceColumn = record['Compliance'] !== undefined ? 'Compliance' :
                                          record.compliance !== undefined ? 'compliance' : null;
                    
                    if (complianceColumn && record[complianceColumn] && 
                        record[complianceColumn].toString().toUpperCase() === 'YES') {
                      monthlyData[monthKey].compliantInspections++;
                    }
                  }
                }
              });
              
              // Convert to array and calculate compliance rate
              return Object.values(monthlyData)
                .map(data => ({
                  month: data.month,
                  totalInspections: data.totalInspections,
                  compliantInspections: data.compliantInspections,
                  complianceRate: data.totalInspections > 0 ? (data.compliantInspections / data.totalInspections) * 100 : 0
                }))
                .sort((a, b) => a.month.localeCompare(b.month));
            })()}
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" tick={{ fill: '#2196f3' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#ff9800' }} />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'Total Inspections') {
                  return [`${value.toLocaleString()}`, 'Total'];
                } else if (name === 'Compliant Inspections') {
                  return [`${value.toLocaleString()}`, 'Compliant'];
                } else if (name === 'Compliance Rate (%)') {
                  return [`${value.toFixed(1)}%`, 'Rate'];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend 
              onClick={(entry) => {
                const lineType = entry.dataKey;
                setHiddenInspectionLines(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(lineType)) {
                    newSet.delete(lineType);
                  } else {
                    newSet.add(lineType);
                  }
                  return newSet;
                });
              }}
            />
            <Bar 
              yAxisId="left"
              dataKey="totalInspections" 
              fill="#2196f3" 
              name="Total Inspections"
              hide={hiddenInspectionLines.has('totalInspections')}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="complianceRate" 
              stroke="#ff9800" 
              strokeWidth={2}
              name="Compliance Rate (%)"
              dot={{ fill: '#ff9800', strokeWidth: 2, r: 4 }}
              hide={hiddenInspectionLines.has('complianceRate')}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Inspections Not in Compliance - Only show if there's data for at least one filter */}
      {hasAnyInspections && (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: 1, borderColor: 'divider' }} data-table-title={
          validInspectionsFilter === 'all' ? 'All Inspections' : 'Inspections Not in Compliance'
      } data-table-type="main">
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {validInspectionsFilter === 'all' ? 'All Inspections' : 'Inspections Not in Compliance'}
            </Typography>
            <Tooltip title="Reset Table" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  onResetInspectionsNotCompliant();
                  resetInspectionsFilter();
                  resetHiddenInspectionLines();
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {/* Only show toggle if both options are available */}
          {hasInspectionsNotCompliant && hasInspectionsAll && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <ToggleButtonGroup
                value={validInspectionsFilter}
              exclusive
              onChange={(event, newFilter) => {
                if (newFilter !== null) {
                  setInspectionsFilter(newFilter);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="not_compliant">
                Not in Compliance
              </ToggleButton>
              <ToggleButton value="all">
                All
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          )}
        </Box>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortInspectionsNotCompliant('lease_facility_name')}
                >
                  Lease Facility Name
                  {inspectionsNotCompliantSortField === 'lease_facility_name' && (
                    <span style={{ marginLeft: 8 }}>
                      {inspectionsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortInspectionsNotCompliant('inspection_date')}
                >
                  Inspection Date
                  {inspectionsNotCompliantSortField === 'inspection_date' && (
                    <span style={{ marginLeft: 8 }}>
                      {inspectionsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortInspectionsNotCompliant('compliance')}
                >
                  Compliance
                  {inspectionsNotCompliantSortField === 'compliance' && (
                    <span style={{ marginLeft: 8 }}>
                      {inspectionsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Filter inspections based on user selection
                const filteredInspections = inspectionsData.filter(record => {
                  if (validInspectionsFilter === 'all') {
                    return true; // Show all inspections
                  }
                  
                  // Filter for inspections not in compliance (Compliance = NO)
                  const complianceColumn = record['Compliance'] !== undefined ? 'Compliance' :
                                         record.compliance !== undefined ? 'compliance' : null;
                  
                  return complianceColumn && record[complianceColumn] && 
                         record[complianceColumn].toString().toUpperCase() === 'NO';
                });

                // Sort the filtered inspections
                filteredInspections.sort((a, b) => {
                  let aValue, bValue;
                  
                  switch (inspectionsNotCompliantSortField) {
                    case 'lease_facility_name':
                      aValue = (a['Lease Facility Name'] || a.lease_facility_name || '').toLowerCase();
                      bValue = (b['Lease Facility Name'] || b.lease_facility_name || '').toLowerCase();
                      break;
                    case 'inspection_date':
                      aValue = new Date(a['Inspection Date'] || a.inspection_date || '');
                      bValue = new Date(b['Inspection Date'] || b.inspection_date || '');
                      break;
                    case 'compliance':
                      const aComplianceColumn = a['Compliance'] !== undefined ? 'Compliance' :
                                             a.compliance !== undefined ? 'compliance' : null;
                      const bComplianceColumn = b['Compliance'] !== undefined ? 'Compliance' :
                                             b.compliance !== undefined ? 'compliance' : null;
                      aValue = aComplianceColumn ? (a[aComplianceColumn] || '').toString().toLowerCase() : '';
                      bValue = bComplianceColumn ? (b[bComplianceColumn] || '').toString().toLowerCase() : '';
                      break;
                    default:
                      aValue = new Date(a['Inspection Date'] || a.inspection_date || '');
                      bValue = new Date(b['Inspection Date'] || b.inspection_date || '');
                  }
                  
                  if (inspectionsNotCompliantSortDirection === 'asc') {
                    return aValue > bValue ? 1 : -1;
                  } else {
                    return aValue < bValue ? 1 : -1;
                  }
                });

                // Apply pagination
                const startIndex = page * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedData = filteredInspections.slice(startIndex, endIndex);

                return paginatedData.map((record, index) => {
                  // Get compliance value
                  const complianceColumn = record['Compliance'] !== undefined ? 'Compliance' :
                                         record.compliance !== undefined ? 'compliance' : null;
                  const complianceValue = complianceColumn ? record[complianceColumn] : null;
                  
                  return (
                  <TableRow key={startIndex + index}>
                    <TableCell>
                      {record['Lease Facility Name'] || record.lease_facility_name || null}
                    </TableCell>
                    <TableCell>
                      {record['Inspection Date'] || record.inspection_date || null}
                    </TableCell>
                      <TableCell>
                        {complianceValue || null}
                      </TableCell>
                  </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {(() => {
          // Calculate total rows for pagination
          const filteredInspections = inspectionsData.filter(record => {
            if (validInspectionsFilter === 'all') {
              return true;
            }
            const complianceColumn = record['Compliance'] !== undefined ? 'Compliance' :
                                   record.compliance !== undefined ? 'compliance' : null;
            return complianceColumn && record[complianceColumn] && 
                   record[complianceColumn].toString().toUpperCase() === 'NO';
          });
          
          return (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredInspections.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  justifyContent: 'center',
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
              }}
            />
          );
        })()}
      </Paper>
      )}
    </Box>
  );
};

export default InspectionsCharts;